import { ChangeEvent, useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation } from "react-query"

import AddTeamMember from "./AddTeamMember"
import { ApiError } from "api/http/utils"
import Button from "ui/buttons/Button"
import { Column } from "react-table"
import { EmptyState } from "components/EmptyState"
import { LocalSearch } from "../../TeamMembers"
import { Table } from "components/tables"
import { TeamMember } from "models/common"
import { ReactComponent as TeamsIcon } from "assets/icon-team.svg"
import { columns } from "../../TeamMembers/columns"
import { deleteAgencyTeamMember } from "api/team"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"
import { useAgencyId } from "hooks/useAgencyId"

type Member = TeamMember & { name: string }

function Members({
  data,
  teamId,
  isLoading,
}: {
  data: Array<Member>
  teamId: string
  isLoading: boolean
}) {
  const { t } = useI18n()
  const feedback = useSendFeedBack()
  const { open, onOpen, onClose } = useToggle(false)
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [searchValue, setSearchValue] = useState<string>("")
  const { hasAuthority } = useAuthenticated()
  const { agencyId } = useAgencyId()

  const handleSearchChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    setSearchValue(() => value)
  }

  const handleSearchClear = () => {
    setSearchValue(() => "")
  }

  const filteredValue =
    data?.length && !!searchValue
      ? data?.filter((d) =>
          d?.name.toLowerCase().includes(searchValue.toLowerCase()),
        )
      : data

  // ** delete team **
  const [deleteMember] = useMutation(deleteAgencyTeamMember, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(`agency-team-${agencyId}`)
      queryCache.invalidateQueries("agency-available-users")
    },
    onError: (e: ApiError) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      feedback(t(errorMap.users) || t("teams.delete.error"), "error")
      setModalInfo(undefined)
    },
  })

  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("teams.member.list.delete.title"),
        type: "primary_destructive",
        description: t("teams.member.list.delete.description"),
        submitLabel: t("teams.member.list.delete.action"),
        onSubmit: async () => await deleteMember({ teamId, memberId: id }),
      })
    },
    [deleteMember, teamId, t],
  )

  const isModalOpen = Boolean(modalInfo)
  const cols = columns(t, onDelete, hasAuthority) as Array<Column<Member>>

  return (
    <>
      <Table<Member>
        id="supplier-groups-list-table"
        columns={cols}
        data={filteredValue ?? []}
        filters={[]}
        total={data?.length || 0}
        loading={isLoading}
        hasColumnsReordering={false}
        localSearch={
          <LocalSearch
            searchValue={searchValue}
            handleSearchChange={handleSearchChange}
            handleSearchClear={handleSearchClear}
          />
        }
        actionButton={
          hasAuthority(["AUTH_EDIT_TEAM"]) && (
            <Button variant="contained" color="primary" onClick={onOpen}>
              {t("member.create.button")}
            </Button>
          )
        }
        noResults={
          <EmptyState
            icon={<TeamsIcon />}
            title={t("teams.noresults.title")}
            subtitle={
              hasAuthority(["AUTH_EDIT_TEAM"])
                ? t("teams.noresults.subtitle")
                : undefined
            }
          >
            {hasAuthority(["AUTH_EDIT_TEAM"]) ? (
              <span
                className={cx(
                  "BodyBody-1Primary-mainRegular",
                  styles.createNew,
                )}
                onClick={onOpen}
              >
                {t("agency.teams.add_new")}
              </span>
            ) : null}
          </EmptyState>
        }
        isPaginated={false}
      />
      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={isModalOpen}
        {...modalInfo}
      />
      <AddTeamMember teamId={teamId} open={open} onClose={onClose} />
    </>
  )
}

const styles = {
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}

export default Members
